import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import Loading from '../components/Loading';
import { verifyEmail } from '../api/endpoints';
import { VerificationExpiredError } from '../api/config';
import { navigateTo } from '../utils/navigation';

export default function VerifyEmailCallbackPage(): React.JSX.Element {
  const params = new URLSearchParams(useLocation().search);
  const token = params.get('token');
  const [state, setState] =
    useState<'loading' | 'success' | 'invalid' | 'error' | 'missing'>('loading');

  const [seconds, setSeconds] = useState(5);

  useEffect(() => {
    if (!token) {
      setState('missing');
      return;
    }
    verifyEmail(token)
      .then((res) => { setState(res.verified ? 'success' : 'invalid'); })
      .catch((error: unknown) => {
        if (error instanceof VerificationExpiredError) setState('invalid');
        else setState('error');
      });
  }, [token]);

  useEffect(() => {
    if (state !== 'success') return;
    if (seconds <= 0) {
      navigateTo('/login');
      return;
    }
    const t = setTimeout(() => { setSeconds((s) => s - 1); }, 1000);
    return () => { clearTimeout(t); };
  }, [state, seconds]);

  if (state === 'loading') return <Loading />;

  if (state === 'success') {
    return (
      <div className="auth-page">
        <div className="orb" aria-hidden style={{ position: 'absolute', bottom: -80, right: -80, opacity: 0.4 }} />
        <div style={{ width: '100%', maxWidth: 440, position: 'relative', zIndex: 1 }}>
          <div className="card stack-5" style={{ textAlign: 'center' }}>
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <div className="status-icon status-icon--teal" aria-hidden>
                <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M20 6L9 17l-5-5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
            </div>
            <div className="stack-2">
              <h1 className="t-h3" style={{ margin: 0 }}>Email <em className="t-italic">verified</em></h1>
              <p className="t-small t-mute" style={{ margin: 0 }}>
                Your address is confirmed. You can now log in and start using Kivo.
              </p>
            </div>
            <div className="row-3" style={{ justifyContent: 'center' }}>
              <button className="btn btn--primary" onClick={() => { navigateTo('/login'); }}>Go to login</button>
              <button className="btn btn--ghost" onClick={() => { navigateTo('/'); }}>Back to home</button>
            </div>
            <p className="t-small t-mute" style={{ margin: 0 }}>
              Redirecting to login in{' '}
              <span style={{ fontVariantNumeric: 'tabular-nums' }}>{seconds}s</span>.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (state === 'invalid' || state === 'missing') {
    return (
      <div className="auth-page">
        <div className="orb" aria-hidden style={{ position: 'absolute', bottom: -80, right: -80, opacity: 0.4 }} />
        <div style={{ width: '100%', maxWidth: 440, position: 'relative', zIndex: 1 }}>
          <div className="card stack-5" style={{ textAlign: 'center' }}>
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <div className="status-icon status-icon--sand" aria-hidden>
                <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
            </div>
            <div className="stack-2">
              <h1 className="t-h3" style={{ margin: 0 }}>Link <em className="t-italic">invalid</em> or expired</h1>
              <p className="t-small t-mute" style={{ margin: '0 auto', maxWidth: '40ch' }}>
                This can happen if the link was already used, expired, or copied incorrectly.
                Log in to request a new verification email, or create a new account.
              </p>
            </div>
            <div className="row-3" style={{ justifyContent: 'center' }}>
              <button className="btn btn--primary" onClick={() => { navigateTo('/login'); }}>Go to login</button>
              <button className="btn btn--ghost" onClick={() => { navigateTo('/register'); }}>Register</button>
            </div>
            <p className="t-small t-mute" style={{ margin: 0 }}>
              Check Spam/Junk and Promotions, and allow{' '}
              <code style={{ fontFamily: 'ui-monospace, monospace', fontSize: 'inherit' }}>no-reply@kivo.skin</code>.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <div className="orb" aria-hidden style={{ position: 'absolute', bottom: -80, right: -80, opacity: 0.4 }} />
      <div style={{ width: '100%', maxWidth: 440, position: 'relative', zIndex: 1 }}>
        <div className="card stack-5" style={{ textAlign: 'center' }}>
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <div className="status-icon status-icon--coral" aria-hidden>
              <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 6L6 18M6 6l12 12" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
          </div>
          <div className="stack-2">
            <h1 className="t-h3" style={{ margin: 0 }}>Something went <em className="t-italic">wrong</em></h1>
            <p className="t-small t-mute" style={{ margin: 0 }}>
              Please try again. If the issue persists, come back later.
            </p>
          </div>
          <div className="row-3" style={{ justifyContent: 'center' }}>
            <button
              className="btn btn--primary"
              onClick={() => { navigateTo(window.location.pathname + window.location.search); }}
            >
              Retry
            </button>
            <button className="btn btn--ghost" onClick={() => { navigateTo('/email-verification/pending'); }}>
              Resend verification
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
